import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { makeTextFieldIcon } from 'shared/ui/TextField'
import { type IResponseRcsCompliance } from 'entities/Rcs'
import { SendFrom } from 'widgets/SendFrom'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import { RcsInfoFormRow } from 'pages/settings/pages/rcsV2/ui'
import { RcsMediaValue, RcsViewLink, RcsViewSection, RcsViewValue } from './RcsViewParts'

import styles from './styles.module.scss'

type ISenderDetailsTabProps = {
  submission: IResponseRcsCompliance
}

const FALLBACK_TOOLTIP = (
  <>
    Some devices don’t support RCS.
    <br />
    Add a fallback inbox to ensure your
    <br />
    messages get delivered.
  </>
)

export const SenderDetailsTab = observer(({ submission }: ISenderDetailsTabProps) => {
  const { init, sendFromStore } = useRcsWizardContext().fallbackStore

  useEffect(() => {
    init()
  }, [])

  return (
    <>
      <RcsViewSection title='General info'>
        <RcsInfoFormRow label='Name'>
          <RcsViewValue>{submission.name}</RcsViewValue>
        </RcsInfoFormRow>
        <RcsInfoFormRow label='Description'>
          <RcsViewValue>{submission.description}</RcsViewValue>
        </RcsInfoFormRow>
        <RcsInfoFormRow label='Message type'>
          <RcsViewValue>{submission.message_type?.label}</RcsViewValue>
        </RcsInfoFormRow>
        <RcsInfoFormRow label='Fallback' variant='field' tooltipProps={{ label: FALLBACK_TOOLTIP }}>
          <SendFrom
            triggerVariant='textField'
            store={sendFromStore}
            width={418}
            textFiledTriggerProps={{
              textFieldPlaceholderProps: {
                variant: 'stroke',
                size: 'small',
                rightActions: [makeTextFieldIcon({ icon: 'chevronDown' })],
              },
            }}
          />
        </RcsInfoFormRow>
      </RcsViewSection>

      <RcsViewSection title='Colors and images'>
        {submission.logo_path && (
          <RcsInfoFormRow label='Logo' variant='media' copyValue={submission.logo_path}>
            <RcsMediaValue url={submission.logo_path} />
          </RcsInfoFormRow>
        )}
        {submission.banner_path && (
          <RcsInfoFormRow label='Banner' variant='media' copyValue={submission.banner_path}>
            <RcsMediaValue url={submission.banner_path} />
          </RcsInfoFormRow>
        )}
        {submission.accent_color && (
          <RcsInfoFormRow label='Accent color' variant='media'>
            <div className={styles.colorValue}>
              <span className={styles.colorSwatch} style={{ color: submission.accent_color }} />
              <RcsViewValue>{submission.accent_color}</RcsViewValue>
            </div>
          </RcsInfoFormRow>
        )}
      </RcsViewSection>

      <RcsViewSection title='Contact info'>
        {submission.contact_phones?.map((phone) => (
          <RcsInfoFormRow key={phone.phone_number} label={phone.label || 'Phone'}>
            <RcsViewValue>{phone.phone_number}</RcsViewValue>
          </RcsInfoFormRow>
        ))}
        {submission.contact_emails?.map((email) => (
          <RcsInfoFormRow key={email.email} label={email.label || 'Email'}>
            <RcsViewValue>{email.email}</RcsViewValue>
          </RcsInfoFormRow>
        ))}
        {submission.website_urls?.map((website) => (
          <RcsInfoFormRow key={website.website} label={website.label || 'Website'}>
            <RcsViewLink href={website.website} />
          </RcsInfoFormRow>
        ))}
        {submission.privacy_policy_url && (
          <RcsInfoFormRow label='Privacy policy URL'>
            <RcsViewLink href={submission.privacy_policy_url} />
          </RcsInfoFormRow>
        )}
        {submission.terms_url && (
          <RcsInfoFormRow label='Terms and conditions URL'>
            <RcsViewLink href={submission.terms_url} />
          </RcsInfoFormRow>
        )}
      </RcsViewSection>
    </>
  )
})
